import { isOutsideOfReportingGracePeriod, OLDEST_TIME } from '@customer/config/time';
import { useLocaleFormatter } from '@customer/shared/hooks/useLocaleFormatter';
import { AssetModel } from '@customer/store/apis/assetsApi';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export interface ReportingItem {
  evt: string;
  dt: DateTime;
  date: string;
  time: string;
  when: string;
  old: boolean;
  error: boolean;
}

export interface ReportingStatus {
  subtext: string;
  good: boolean;
  items?: ReportingItem[];
}

interface UseAssetReportingTableDataProps {
  asset?: AssetModel;
}

export const useAssetReportingTableData = ({
  asset,
}: UseAssetReportingTableDataProps): ReportingStatus => {
  const { t } = useTranslation();
  const { showDateOnly, showTimeOnly, showRelative, parseFromIso } = useLocaleFormatter();

  return useMemo<ReportingStatus>(() => {
    if (!asset) {
      return {
        subtext: '',
        good: false,
        items: undefined,
      };
    }

    const sevenDaysAgo = DateTime.now().minus({ days: 7 });
    const minTime = DateTime.fromISO(OLDEST_TIME);

    const generateItem = (desc: string, ts?: string): ReportingItem => {
      if (ts) {
        const parsedTime = parseFromIso(ts);
        const tooOld = parsedTime < sevenDaysAgo;

        return {
          evt: desc,
          dt: parsedTime,
          date: showDateOnly(parsedTime),
          time: showTimeOnly(parsedTime),
          when: showRelative(parsedTime) ?? '-',
          old: tooOld,
          error: false,
        };
      }

      return {
        evt: desc,
        dt: minTime,
        date: '-',
        time: '-',
        when: '-',
        old: true,
        error: false,
      };
    };

    const itemReported = generateItem(t('LastReported'), asset.ts);
    const itemConnected = generateItem(t('LastConnected'), asset.tsRx);
    const itemGoodReport = generateItem(t('LastGoodReport'), asset.posG?.ts);
    const itemSeen = generateItem(t('LastSeen'), asset.posS?.ts);

    const items: ReportingItem[] = [itemGoodReport, itemConnected, itemSeen];

    if (isOutsideOfReportingGracePeriod(itemGoodReport.dt, itemReported.dt)) {
      items.push(itemReported);
    }

    let statusGood = true;
    let subtext = t('NoIssuesDetected');

    if (itemReported.old || itemConnected.old) {
      if (itemSeen.old) {
        subtext = t('NotReportingInactive');
      } else {
        subtext = t('NotReportingActive');
        itemSeen.error = true;
      }

      statusGood = false;
    }

    return {
      subtext: subtext,
      good: statusGood,
      items: items.sort((a, b) => (a.dt < b.dt ? 1 : -1)),
    };
  }, [asset, t, parseFromIso, showDateOnly, showTimeOnly, showRelative]);
};
